import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { crmService } from '../../services/crmService';
import { leadsPostImportUrl } from '../../utils/crmLinks';

export type CsvImportObjectType = 'lead' | 'prospect' | 'client';

const TYPE_LABELS: Record<CsvImportObjectType, string> = {
  lead: 'Leads (pipeline)',
  prospect: 'Prospects',
  client: 'Clients',
};

interface CsvImportWizardProps {
  open: boolean;
  onClose: () => void;
  /** Pre-select import target; user can still change unless `lockObjectType`. */
  defaultObjectType: CsvImportObjectType;
  lockObjectType?: boolean;
  onImported?: () => void;
}

type Step = 'upload' | 'map' | 'done';

const IGNORE = '__ignore__';
const fallbackHeaderLabel = (header: string, index: number) => (header?.trim() ? header : `Column ${index + 1}`);

const CsvImportWizard: React.FC<CsvImportWizardProps> = ({
  open,
  onClose,
  defaultObjectType,
  lockObjectType,
  onImported,
}) => {
  const [step, setStep] = useState<Step>('upload');
  const [objectType, setObjectType] = useState<CsvImportObjectType>(defaultObjectType);
  const [file, setFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [sampleRows, setSampleRows] = useState<Record<string, string>[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [fieldDefs, setFieldDefs] = useState<Record<string, { label: string; required: boolean }>>({});
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [executeResult, setExecuteResult] = useState<{
    imported_rows: number;
    skipped_rows: number;
    failed_rows: number;
    failure_reason_counts: Record<string, number>;
    failures_sample: Array<{ row_index: number; errors: string[] }>;
    failed_rows_csv: string;
    object_type: string;
  } | null>(null);

  const fieldOptions = useMemo(() => {
    const opts = Object.entries(fieldDefs).map(([value, meta]) => ({
      value,
      label: meta.label,
    }));
    return [{ value: IGNORE, label: '— Ignore column —' }, ...opts];
  }, [fieldDefs]);

  const reset = useCallback(() => {
    setStep('upload');
    setObjectType(defaultObjectType);
    setFile(null);
    setJobId(null);
    setHeaders([]);
    setSampleRows([]);
    setRowCount(0);
    setFieldDefs({});
    setMapping({});
    setError('');
    setExecuteResult(null);
    setLoading(false);
  }, [defaultObjectType]);

  if (!open) return null;

  const handleClose = () => {
    reset();
    onClose();
  };

  const handlePreview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Choose a .csv file.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('object_type', objectType);
      const res = await crmService.csvImportPreview(fd);
      const previewHeaders = Array.isArray(res.headers) ? res.headers : [];
      setJobId(res.job_id);
      setHeaders(previewHeaders);
      setSampleRows(res.sample_rows || []);
      setRowCount(res.row_count);
      setFieldDefs(res.field_defs || {});
      const m: Record<string, string> = { ...res.suggested_mapping };
      for (const h of previewHeaders) {
        if (m[h] === undefined) m[h] = IGNORE;
      }
      setMapping(m);
      setStep('map');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!jobId) return;
    const used = new Set<string>();
    for (const h of headers) {
      const t = mapping[h];
      if (t && t !== IGNORE) {
        if (used.has(t)) {
          setError(`Target field "${fieldDefs[t]?.label ?? t}" is mapped more than once.`);
          return;
        }
        used.add(t);
      }
    }
    setLoading(true);
    setError('');
    try {
      const res = await crmService.csvImportExecute({ job_id: jobId, column_mapping: mapping });
      setExecuteResult({
        imported_rows: res.imported_rows,
        skipped_rows: res.skipped_rows,
        failed_rows: res.failed_rows,
        failure_reason_counts: res.failure_reason_counts || {},
        failures_sample: res.failures_sample || [],
        failed_rows_csv: res.failed_rows_csv || '',
        object_type: res.object_type,
      });
      setStep('done');
      onImported?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadFailures = () => {
    if (!executeResult?.failed_rows_csv) return;
    const blob = new Blob([executeResult.failed_rows_csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'import-failures.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const listPath =
    executeResult?.object_type === 'prospect'
      ? '/prospects'
      : executeResult?.object_type === 'client'
        ? '/clients'
        : '/leads';

  return (
    <div className="modal-backdrop z-50">
      <div className="relative top-6 mx-auto max-h-[90vh] w-full max-w-3xl overflow-y-auto border border-border bg-surface p-5 shadow-lg rounded-2xl">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-fg">Import contacts from a spreadsheet</h3>
            <p className="text-sm text-fg-muted mt-1">
              Bring your existing list into LaunchPoint in three steps: upload → match columns → import. Rows that
              already exist (same email or phone in your account) are skipped automatically.
            </p>
          </div>
          <button type="button" onClick={handleClose} className="text-fg-muted hover:text-fg" aria-label="Close">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error ? <p className="mb-3 text-sm text-danger-fg">{error}</p> : null}

        {step === 'upload' && (
          <form onSubmit={handlePreview} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-fg mb-2">Import as</label>
              <select
                value={objectType}
                disabled={lockObjectType}
                onChange={(e) => setObjectType(e.target.value as CsvImportObjectType)}
                className="input-field w-full rounded-xl border border-border bg-input px-3 py-2 text-fg disabled:opacity-60"
              >
                {(Object.keys(TYPE_LABELS) as CsvImportObjectType[]).map((k) => (
                  <option key={k} value={k}>
                    {TYPE_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-fg mb-2">CSV file</label>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-fg-muted file:mr-3 file:rounded-lg file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-fg"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={handleClose} className="btn-surface-muted rounded-xl px-4 py-2 text-sm">
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-fg disabled:opacity-50"
              >
                {loading ? 'Reading…' : 'Next: map columns'}
              </button>
            </div>
          </form>
        )}

        {step === 'map' && (
          <div className="space-y-4">
            <p className="text-sm text-fg-muted">
              {rowCount} data row{rowCount === 1 ? '' : 's'}. Match each column to a field or ignore it.
            </p>
            {headers.length === 0 ? (
              <p className="text-sm text-danger-fg">No columns were detected in this CSV preview.</p>
            ) : null}
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-elevated/50">
                    <th className="px-3 py-2 text-left font-medium text-fg">CSV column</th>
                    <th className="px-3 py-2 text-left font-medium text-fg">Import as</th>
                  </tr>
                </thead>
                <tbody>
                  {headers.map((h, colIndex) => (
                    <tr key={`map-${colIndex}`} className="border-b border-border/80">
                      <td className="px-3 py-2 text-fg align-top">{fallbackHeaderLabel(h, colIndex)}</td>
                      <td className="px-3 py-2">
                        <select
                          value={mapping[h] ?? IGNORE}
                          onChange={(e) => setMapping((prev) => ({ ...prev, [h]: e.target.value }))}
                          className="input-field w-full max-w-xs rounded-lg border border-border bg-input px-2 py-1.5 text-fg"
                        >
                          {fieldOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sampleRows.length > 0 ? (
              <div>
                <p className="text-xs font-medium text-fg-muted mb-1">Sample rows</p>
                <div className="overflow-x-auto rounded-lg border border-border text-xs font-mono max-h-40 overflow-y-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr>
                        {headers.map((h, colIndex) => (
                          <th key={`sample-head-${colIndex}`} className="px-2 py-1 text-left text-fg-muted border-b border-border">
                            {fallbackHeaderLabel(h, colIndex)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sampleRows.map((row, i) => (
                        <tr key={i}>
                          {headers.map((h, colIndex) => (
                            <td key={`sample-cell-${i}-${colIndex}`} className="px-2 py-1 border-b border-border/60 text-fg">
                              {(row[h] ?? '').slice(0, 80)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
            <div className="flex justify-between gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setStep('upload');
                  setError('');
                }}
                className="btn-surface-muted rounded-xl px-4 py-2 text-sm"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleExecute}
                disabled={loading}
                className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-fg disabled:opacity-50"
              >
                {loading ? 'Importing…' : 'Run import'}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && executeResult && (
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-surface-elevated/30 p-4">
              <h4 className="font-semibold text-fg mb-2">Import finished</h4>
              <ul className="text-sm text-fg space-y-1">
                <li>Imported: {executeResult.imported_rows}</li>
                <li>Skipped (duplicates): {executeResult.skipped_rows}</li>
                <li>Failed: {executeResult.failed_rows}</li>
              </ul>
              {Object.keys(executeResult.failure_reason_counts).length > 0 ? (
                <div className="mt-3 text-xs text-fg-muted">
                  <p className="font-medium text-fg mb-1">Top issues</p>
                  <ul className="list-disc pl-4">
                    {Object.entries(executeResult.failure_reason_counts)
                      .slice(0, 5)
                      .map(([reason, n]) => (
                        <li key={reason}>
                          {reason} ({n})
                        </li>
                      ))}
                  </ul>
                </div>
              ) : null}
              {executeResult.failures_sample.length > 0 ? (
                <div className="mt-3 text-xs">
                  <p className="font-medium text-fg mb-1">Sample errors</p>
                  <ul className="space-y-1 text-fg-muted">
                    {executeResult.failures_sample.slice(0, 5).map((f) => (
                      <li key={f.row_index}>
                        Row {f.row_index}: {f.errors.join('; ')}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2 justify-between">
              {executeResult.failed_rows_csv ? (
                <button
                  type="button"
                  onClick={downloadFailures}
                  className="rounded-xl border border-border px-4 py-2 text-sm text-fg hover:bg-surface-elevated/50"
                >
                  Download failed rows (CSV)
                </button>
              ) : (
                <span />
              )}
              <div className="flex flex-wrap gap-2 justify-end">
                {executeResult.object_type === 'lead' && executeResult.imported_rows > 0 ? (
                  <Link
                    to={leadsPostImportUrl()}
                    className="rounded-xl border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-elevated/50"
                    onClick={handleClose}
                  >
                    Review by priority
                  </Link>
                ) : null}
                <Link
                  to={listPath}
                  className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-fg"
                  onClick={handleClose}
                >
                  {executeResult.imported_rows > 0 ? 'View imported records' : 'Go to list'}
                </Link>
                <button type="button" onClick={handleClose} className="btn-surface-muted rounded-xl px-4 py-2 text-sm">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CsvImportWizard;
